/**
 * Runtime bridge between the main bundle and the lazily-loaded side chunk
 * (sdk-x.js: clicks + mappings + vitals + search — see tsup.config.ts).
 * Property names here cross two independently-mangled bundles, so they are
 * 1-letter and NOT mangled (no trailing underscore).
 */
import type {
	AutoTrackConfig,
	BridgeRule,
	FormConfig,
	IdentityConfig,
	MappingRule,
	WireNetRequest,
} from '@l3ns/shared/types';
import { LIMITS } from '@l3ns/shared/constants';
import { safe } from '../core/safe';
import { st } from '../core/state';
import { flush } from '../core/transport';
import { enqueue } from '../core/track';
import { setUser } from '../core/user';
import { ping } from './activity';
import { repErrEvent } from './errors';
import { evH, navH, visH } from './util';

export interface XApi {
	/** enqueue(name, props) */
	q: (name: string, props?: Record<string, unknown>) => void;
	/** auto-track flag AND consent, read at emit time — capture gate for every module */
	f: (k: keyof AutoTrackConfig) => boolean;
	/** privacy.maskText, read at emit time */
	m: () => boolean;
	/** mapping rules from remote config */
	g: () => MappingRule[];
	/** per-form operator config: step selector, success URL / event (remote config) */
	c: () => FormConfig[];
	z: () => Array<{ key: string; selector: string; urlPattern?: string }>;
	/** event bridges from remote config (third-party emitters → events) */
	i: () => BridgeRule[];
	/** user identity config from remote config (undefined = manual identify() only) */
	j: () => IdentityConfig | undefined;
	/** attach the signed-in user to the session (no id = sign out) — see core/user.ts */
	u: (id?: unknown, name?: unknown) => void;
	/** flush the queue immediately (picker sends must not wait for the timer) */
	y: () => void;
	/** register a visibility hook (hidden, unload = pagehide) — runs before flush */
	v: (fn: (hidden: boolean, unload?: boolean) => void) => void;
	/** register a per-page-view reset hook */
	n: (fn: () => void) => void;
	/** register an event hook: called with every queued event's name */
	h: (fn: (name: string) => void) => void;
	/** activity ping */
	p: () => void;
	/** merge self-instrumentation counters into diag() output */
	d: (patch: Record<string, number>) => void;
	/** safe() wrapper */
	e: <A extends unknown[], R>(fn: (...a: A) => R) => (...a: A) => R | undefined;
	/** push a network waterfall row (bounded; flushes when a batch is ready) */
	r: (row: WireNetRequest) => void;
	/** ingest API origin — net capture must skip the SDK's own calls */
	a: () => string;
	/** remote config settled — buffered capture waits for it before emitting */
	k: () => boolean;
	/** report an error-family event under the core dedupe/per-page budget */
	b: (name: string, msg: string, props: Record<string, unknown>) => void;
}

export function mkXApi(): XApi {
	return {
		q: enqueue,
		// consent folded in: no module should CAPTURE without it (enqueue drops
		// events anyway, but net rows buffer until flush and would ship later)
		f: (k) => st.consent_ && !!st.auto_[k],
		m: () => st.privacy_.maskText,
		g: () => st.mappings_,
		c: () => st.formSteps_,
		z: () => st.zones_,
		i: () => st.bridges_,
		j: () => st.identity_,
		u: setUser,
		y: () => flush('picker'),
		v: (fn) => visH.push(fn),
		n: (fn) => navH.push(fn),
		h: (fn) => evH.push(fn),
		p: ping,
		d: (patch) => Object.assign(st.diag_, patch),
		e: safe,
		r: (row) => {
			if (!st.consent_) return; // belt-and-braces: capture already gates on o()
			if (st.n_.length >= 400) {
				st.n_.shift();
				st.diag_.dropped++;
			}
			st.n_.push(row);
			if (st.n_.length >= LIMITS.MAX_NET_PER_ENVELOPE) flush('network');
		},
		a: () => st.api_,
		k: () => st.cfg_,
		b: repErrEvent,
	};
}

// captured at bundle eval — the classic <script> tag, if that's how we loaded
const CS = document.currentScript as HTMLScriptElement | null;

/**
 * Lazily load the side chunk from the main script's own directory. No script
 * tag (ESM/npm build) ⇒ no chunk — documented gap in the README; the ≤10 KB
 * budget applies to the script-tag artifact.
 */
export function loadX(): void {
	if (CS?.src)
		void (
			import(/* @vite-ignore */ new URL('sdk-x.js', CS.src).href) as Promise<{
				default: (x: XApi) => void;
			}>
		)
			.then((m) => m.default(mkXApi()))
			.catch(() => {});
}
